import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { parseContentToSlides } from './parseContentToSlides';
import PresenterControls from './PresenterControls';
import SlideRenderer from './SlideRenderer';

interface PresenterProps {
  content: string;
}

export default function Presenter({ content }: PresenterProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const slides = useMemo(() => parseContentToSlides(content), [content]);

  // Clamp index when slide count changes
  useEffect(() => {
    setCurrentIndex((prev) => Math.min(prev, Math.max(0, slides.length - 1)));
  }, [slides.length]);

  // Fullscreen lifecycle
  useEffect(() => {
    function onFullscreenChange() {
      setIsFullscreen(!!document.fullscreenElement);
    }
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () =>
      document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current.requestFullscreen();
    }
  }, []);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((i) => Math.max(0, i - 1));
  }, []);

  const goToNext = useCallback(() => {
    setCurrentIndex((i) => Math.min(slides.length - 1, i + 1));
  }, [slides.length]);

  if (slides.length === 0) {
    return (
      <EmptyState>
        <p>No slides to present.</p>
        <p>Add content to your lesson plan to get started.</p>
      </EmptyState>
    );
  }

  return (
    <Container ref={containerRef}>
      <SlideArea>
        <SlideRenderer slide={slides[currentIndex]} slideKey={`slide-${currentIndex}`} />
      </SlideArea>
      <PresenterControls
        currentIndex={currentIndex}
        totalSlides={slides.length}
        isFullscreen={isFullscreen}
        onPrevious={goToPrevious}
        onNext={goToNext}
        onToggleFullscreen={toggleFullscreen}
      />
    </Container>
  );
}

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;

  &:fullscreen {
    background: white;
  }
`;

const SlideArea = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 16px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  font-size: 16px;
  gap: 4px;
`;

